import { useCallback, useState } from "react";

import {
  combineRunProps,
  parseErrorJson,
  parseErrorResponse,
  useRequestWithFeedback,
  useIdempotencyErrorHandling,
} from "../../../composites";
import { useObjectMemo } from "../../../hooks";
import { useSelectedRecipient } from "../../entities/recipients";
import { useInstitution } from "../../entities";
import { useACHCompany } from "../../entities/achCompanies";
import { useLibrary } from "../../../providers";

import {
  getFedNowNetworkPaymentOutcome,
  getFedNowTransactionStage,
  getIsOneTimePaymentForToday,
  getScheduledRecurrenceRuleString,
  getScreenWithError,
} from "./utils";
import { API } from "./api";
import { useIsSingleExpediteEnabled } from "./hooks";
import {
  ACHPaymentForm,
  transformToApiFields,
  transformToFedNowApiFields,
  transformToScheduledApiFields,
} from "./ACHPayment.form";

import type {
  AnyException,
  Data,
  ErrorResponse,
  LayeredOverrideRunProps,
} from "../../../composites";
import type { FedNowTransactionOutcome } from "./utils";

export const useCreateACHPayment = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateACHPayment.Response | API.CreateScheduledACHPayment.Response,
    API.CreateACHPayment.Error | API.CreateScheduledACHPayment.Error
  >();
  const t = useLibrary("translations");
  const { values } = ACHPaymentForm.useForm();
  const recipient = useSelectedRecipient() as API.Recipient;
  const achCompany = useACHCompany() as API.ACHCompany;
  const institution = useInstitution();
  const isSingleExpediteEnabled = useIsSingleExpediteEnabled();
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );

  const createACHPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        onSuccess?: (data: API.ACHPayment | API.ScheduledACHPayment) => void;
        onError?: (screenWithError: {
          screen: number;
          field?: string;
          message: string;
        }) => void;
        deviceIds?: { deviceId: string; deviceRequestId: string };
        invoice?: string;
      } = {},
    ) => {
      const isOneTimePaymentForToday = getIsOneTimePaymentForToday(
        values,
        institution?.time_zone,
      );

      // Use device IDs from overrideRunProps if provided, otherwise use form values
      const deviceId = overrideRunProps.deviceIds?.deviceId ?? values.deviceId;
      const deviceRequestId =
        overrideRunProps.deviceIds?.deviceRequestId ?? values.deviceRequestId;

      const todayOneTimePaymentValues = {
        ...values,
        deviceId,
        deviceRequestId,
        recipient: recipient.id,
        achCompany: achCompany.id,
        invoice: overrideRunProps.invoice,
        isSingleExpediteEnabled,
      };

      const scheduledValues = {
        ...todayOneTimePaymentValues,
        recurringRuleString: getScheduledRecurrenceRuleString(values),
        transactionType: values.transactionType,
      };

      const transformedValues = isOneTimePaymentForToday
        ? transformToApiFields(todayOneTimePaymentValues)
        : transformToScheduledApiFields(scheduledValues);

      send({
        action: API.createACHPayment(idempotencyKey, transformedValues),
        onSuccess: overrideRunProps.onSuccess,
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          const defaultError = t.getString(
            "ach-payment-error-banner",
            null,
            "We could not process your ACH payment. Please try again.",
          );

          const screenWithError = json
            ? getScreenWithError(json, defaultError)
            : { screen: 1, message: defaultError };

          overrideRunProps.onError?.(screenWithError);
        },
      });
    },
    [
      t,
      values,
      recipient,
      achCompany,
      institution?.time_zone,
      isSingleExpediteEnabled,
      send,
      canHandleIdempotencyErrors,
    ],
  );

  return useObjectMemo({
    createACHPayment,
    loading,
  });
};

export const FEDNOW_STATUS_POLL_INTERVAL_MS = 1000;
export const FEDNOW_STATUS_MAX_POLLS = 6;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    setTimeout(resolve, ms);
  });

// FedNow payments are created pending fraud evaluation and only fail or succeed
// later, so poll the transaction and then its FedNow network status.
// Resolves "processing" if the outcome isn't known in time.
export const useAwaitFedNowTransactionOutcome = () => {
  const { send: sendTransaction } = useRequestWithFeedback<
    API.FedNowTransaction,
    AnyException
  >();
  const { send: sendNetworkPayment } = useRequestWithFeedback<
    API.FedNowPayment,
    AnyException
  >();

  // send() resolves undefined when it skips a request because one is in flight
  const getOutcome = useCallback(
    async (
      transaction: API.FedNowTransaction,
    ): Promise<FedNowTransactionOutcome> => {
      const stage = getFedNowTransactionStage(transaction.state);
      if (stage !== "submitted") return stage;

      const networkPayment = transaction.payment_identification
        ? await (
            sendNetworkPayment({
              action: API.fetchFedNowNetworkPayment(
                transaction.payment_identification,
              ),
            }) as Promise<API.FedNowPayment | undefined>
          ).catch(() => undefined)
        : undefined;
      return networkPayment
        ? getFedNowNetworkPaymentOutcome(networkPayment.state)
        : "processing";
    },
    [sendNetworkPayment],
  );

  const awaitFedNowTransactionOutcome = useCallback(
    async (
      transaction: API.FedNowTransaction,
    ): Promise<FedNowTransactionOutcome> => {
      let latest = transaction;
      let outcome = await getOutcome(latest);
      for (
        let attempt = 0;
        outcome === "processing" && attempt < FEDNOW_STATUS_MAX_POLLS;
        attempt += 1
      ) {
        // eslint-disable-next-line no-await-in-loop
        await wait(FEDNOW_STATUS_POLL_INTERVAL_MS);
        // eslint-disable-next-line no-await-in-loop
        const refreshed = await (
          sendTransaction({
            action: API.fetchFedNowTransaction(latest.id),
          }) as Promise<API.FedNowTransaction | undefined>
        ).catch(() => undefined);
        latest = refreshed ?? latest;
        // eslint-disable-next-line no-await-in-loop
        outcome = await getOutcome(latest);
      }
      return outcome;
    },
    [getOutcome, sendTransaction],
  );

  return useObjectMemo({ awaitFedNowTransactionOutcome });
};

export const useCreateFedNowPayment = () => {
  const { send, loading } = useRequestWithFeedback<
    API.CreateFedNowPayment.Response,
    API.CreateFedNowPayment.Error
  >();
  const t = useLibrary("translations");
  const { values } = ACHPaymentForm.useForm();
  const recipient = useSelectedRecipient() as API.Recipient;
  const canHandleIdempotencyErrors = useIdempotencyErrorHandling(
    "payment-idempotency-error",
  );
  const { awaitFedNowTransactionOutcome } = useAwaitFedNowTransactionOutcome();
  const [awaitingOutcome, setAwaitingOutcome] = useState(false);

  const createFedNowPayment = useCallback(
    (
      idempotencyKey: UUID,
      overrideRunProps: {
        // called once the payment's outcome is known, so receipts don't report
        // a payment that FedNow later rejects as sent
        onSuccess?: (
          data: API.FedNowTransaction,
          outcome: FedNowTransactionOutcome,
        ) => void;
        onError?: (error: { message: string }) => void;
        deviceIds?: { deviceId: string; deviceRequestId: string };
        invoice?: string;
      } = {},
    ) => {
      const transformedValues = transformToFedNowApiFields({
        ...values,
        deviceId: overrideRunProps.deviceIds?.deviceId ?? values.deviceId,
        deviceRequestId:
          overrideRunProps.deviceIds?.deviceRequestId ?? values.deviceRequestId,
        recipient: recipient.id,
        invoice: overrideRunProps.invoice,
      });

      send({
        action: API.createFedNowPayment(idempotencyKey, transformedValues),
        onSuccess: async (data) => {
          setAwaitingOutcome(true);
          const outcome = await awaitFedNowTransactionOutcome(data);
          setAwaitingOutcome(false);
          overrideRunProps.onSuccess?.(data, outcome);
        },
        onError: async (error) => {
          const json = await error?.response?.json().catch(() => undefined);

          const { errors } = parseErrorJson(json);
          if (canHandleIdempotencyErrors(errors)) return;

          overrideRunProps.onError?.({
            message:
              errors[0]?.description ||
              t.getString(
                "instant-payment-error-banner",
                null,
                "We could not process your instant payment. Please try again.",
              ),
          });
        },
      });
    },
    [
      t,
      values,
      recipient,
      send,
      canHandleIdempotencyErrors,
      awaitFedNowTransactionOutcome,
    ],
  );

  return useObjectMemo({
    createFedNowPayment,
    loading: loading || awaitingOutcome,
  });
};

export const useDeleteScheduledACHPayment = () => {
  const { send, loading } = useRequestWithFeedback();
  const l10n = useLibrary("translations");
  const { throwToast } = useLibrary("toasts");

  const deleteScheduledACHPayment = useCallback(
    (
      uuid: API.ScheduledACHPaymentId,
      overrideRunProps: LayeredOverrideRunProps<Data, AnyException<Data>> = {},
    ) => {
      return send({
        action: API.deleteScheduledACHPayment(uuid),
        ...combineRunProps(
          {
            onSuccess: async () => {
              throwToast({
                kind: "success",
                message: l10n.getString(
                  "delete-payment-success-message",
                  null,
                  "Payment canceled.",
                ),
              });
            },
            onError: async (error: ErrorResponse<AnyException<Data>>) => {
              const { errors } = await parseErrorResponse(error);
              const defaultError = l10n.getString(
                "delete-payment-error-message",
                null,
                "We could not cancel your payment. Please try again.",
              );
              throwToast({
                kind: "error",
                message: errors.length
                  ? `${errors[0].description}`
                  : defaultError,
              });
            },
          },
          overrideRunProps,
        ),
      });
    },
    [send, l10n, throwToast],
  );

  return useObjectMemo({
    deleteScheduledACHPayment,
    loading,
  });
};
